/** Single source of truth for every display color in the app is
 *  `src/styles.css` (per the rule in AGENTS.md). This helper reads
 *  a CSS custom property at call time so Paper.js / SVG /
 *  text-color call sites can take their value from the same place
 *  the UI chrome does.
 *
 *  Usage:
 *    import { cssVar } from "../colors";
 *    item.strokeColor = new scope.Color(cssVar("--canvas-accent"));
 *
 *  The lookup is `getComputedStyle(document.documentElement)`. It's
 *  cheap once the stylesheet has been applied (which happens before
 *  the first `<script type="module">` runs because the `<link>` is
 *  in `<head>`), but each call still walks the cascade — if a hot
 *  render loop pulls the same var many times, cache it once per
 *  render (Scene does this in `render()` for its color palette). */
export function cssVar(name: string): string {
  if (typeof document === "undefined") return ""; // jsdom / unit-test safety
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  if (!v) {
    // Empty result means either the variable is undefined OR the
    // stylesheet hasn't been applied yet. Surface it as a dev-time
    // warning so the missing var is easy to spot.
    console.warn(
      `cssVar("${name}") returned empty — is the variable defined in styles.css?`,
    );
  }
  return v;
}
